import { useEffect } from "react";

import { isSaasDistribution } from "utils/distribution";

import { Theme } from "../types/Theme";
import { AccountContext } from "../views/AccountWrapper";
import { ThemeContext } from "../views/Theme";
import useTypedContext from "./useTypedContext";

const isSaas = isSaasDistribution();

/**
 * Code here is based on the Remix example from here: https://help.featurebase.app/help/articles/1261560-install-feedback-widget
 */
const initFeaturebase = () => {
  const existingScript = document.getElementById("featurebase-sdk");
  if (existingScript) {
    return;
  }

  const script = document.createElement("script");
  script.src = "https://do.featurebase.app/js/sdk.js";
  script.id = "featurebase-sdk";
  document.head.appendChild(script);

  if (typeof window.Featurebase !== "function") {
    window.Featurebase = function () {
      if (window.Featurebase) {
        (window.Featurebase.q = window.Featurebase.q || []).push(
          // eslint-disable-next-line prefer-rest-params
          arguments
        );
      }
    };
  }
};

const useInitFeaturebase = (showChangelogUpdate: (show: boolean) => void) => {
  const { currentTheme } = useTypedContext(ThemeContext);
  const { viewer, accountId, accountName } = useTypedContext(AccountContext);

  useEffect(() => {
    // Only initialize Featurebase in SaaS environments
    if (!isSaas) return;

    initFeaturebase();
  }, []);

  useEffect(() => {
    if (!isSaas || !viewer) {
      return;
    }

    const email = viewer.email || `unknown+${viewer.id}@unknown.us`;

    window.Featurebase?.(
      "identify",
      {
        email,
        organization: "spacelift",
        name: viewer.name || viewer.email || "Unknown",
        id: viewer.id,
        companies: [
          {
            id: accountId,
            name: accountName,
          },
        ],
      },
      (err) => {
        if (err) {
          console.warn("Featurebase error", err);
        } else {
          window.Featurebase?.("initialize_feedback_widget", {
            organization: "spacelift",
            theme: [Theme.Dark, Theme.HighContrastDark].includes(currentTheme) ? "dark" : "light",
            email,
            locale: "en", // Change the language, view all available languages from https://help.featurebase.app/en/articles/8879098-using-featurebase-in-my-language
            metadata: {
              since: String(viewer.since),
              userId: viewer.id,
              ulid: accountId,
            },
          });

          window.Featurebase?.(
            "init_changelog_widget",
            {
              organization: "spacelift",
              theme: [Theme.Dark, Theme.HighContrastDark].includes(currentTheme) ? "dark" : "light",
              locale: "en", // Change the language, view all available languages from https://help.featurebase.app/en/articles/8879098-using-featurebase-in-my-language
              popup: {
                enabled: true, // Add this to enable the popup view of the changelog
                autoOpenForNewUpdates: false, // This will open the popup for new updates
              },
            },
            (error, data) => {
              if (error) {
                return;
              }
              if (data?.action === "unreadChangelogsCountChanged") {
                if (data.unreadCount > 0) {
                  showChangelogUpdate(true);
                } else {
                  showChangelogUpdate(false);
                }
              }
            }
          );
        }
      }
    );
  }, [accountId, accountName, currentTheme, viewer, showChangelogUpdate]);
};

export default useInitFeaturebase;
